'use client'

import { useState, useMemo, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { analytics } from '@/lib/analytics'
import { getAssetUrl } from '@/utils/env'
import { transformImageUrl } from '@/lib/image-url'
import { HERO_DEMO_ASSET_VERSION } from '@kiltechco/dday-templates/meta'
import type { HeroDemo } from '@kiltechco/dday-templates/demos'
import { SearchInput } from '@/components/ui/Input'
import { ComponentLoader } from '@/components/ui/Loading'
import DesignCard from './DesignCard'
import DesignPreviewContainer from './DesignPreviewContainer'

// Map design IDs to image filenames (single source, CF transforms for sizing)
const designCardThumbnails: Record<string, string> = {
  'our-love-begins': 'our-love-begins-royal-black',
  'forever-yours': 'yours-forever-blush',
  'tying-the-knot': 'tying-the-knot-1',
  'getting-married': 'getting-married-center',
  'found-each-other': 'found-each-other-royal-black',
  'meant-to-be-together': 'meant-to-be-together-black',
  polaroids: 'polaroids',
}

// Get design card thumbnail with CF transformation (200x420 for 2x retina of 100x210)
function getDesignCardThumbnail(designId: string): string | undefined {
  const filename = designCardThumbnails[designId]
  if (!filename) return undefined
  const baseUrl = getAssetUrl(`/assets/demos/heros/${filename}.webp`)
  const transformedUrl = transformImageUrl(baseUrl, {
    width: 200,
    height: 420,
    fit: 'cover',
    quality: 80,
  })
  // Add version param for cache busting
  return `${transformedUrl}?v=${HERO_DEMO_ASSET_VERSION}`
}

interface DigitalDesignGridProps {
  /** Callback when designs are loaded (for parent to track count) */
  onDesignsLoaded?: (count: number) => void
}

export default function DigitalDesignGrid({
  onDesignsLoaded,
}: DigitalDesignGridProps) {
  const router = useRouter()
  const t = useTranslations('designs')
  const [searchQuery, setSearchQuery] = useState('')
  const [previewDesign, setPreviewDesign] = useState<HeroDemo | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [designs, setDesigns] = useState<HeroDemo[]>([])
  const [isLoading, setIsLoading] = useState(true)

  // Track page view
  const hasTrackedPageView = useRef(false)

  // Dynamically load hero demos when component mounts
  useEffect(() => {
    import('@kiltechco/dday-templates/demos').then((module) => {
      // HERO_DEMOS is a Record<string, HeroDemo>, convert to array
      // Override thumbnailUrl with CDN URLs where available
      const demoArray = Object.values(module.HERO_DEMOS).map((design) => ({
        ...design,
        thumbnailUrl: getDesignCardThumbnail(design.id) || design.thumbnailUrl,
      }))
      setDesigns(demoArray)
      setIsLoading(false)
      onDesignsLoaded?.(demoArray.length)

      // Track page view after designs are loaded
      if (!hasTrackedPageView.current) {
        hasTrackedPageView.current = true
        analytics.page.designsPageViewed(demoArray.length)
      }
    })
  }, [onDesignsLoaded])

  // Convert designs to suggestions for autocomplete
  const searchSuggestions = useMemo(
    () => designs.map((design) => ({ id: design.id, label: design.label })),
    [designs]
  )

  // Filter designs based on search query
  const filteredDesigns = useMemo(() => {
    if (!searchQuery.trim()) return designs

    const query = searchQuery.toLowerCase().trim()
    return designs.filter(
      (design) =>
        design.id.toLowerCase().includes(query) ||
        design.label.toLowerCase().includes(query) ||
        design.description?.toLowerCase().includes(query)
    )
  }, [searchQuery, designs])

  const handleDesignSelect = (design: HeroDemo) => {
    // Track template selection
    analytics.builder.templateSelected(design.id, design.label)
    // Navigate to builder with heroId
    router.push(`/builder?heroId=${design.id}`)
  }

  const handleDesignPreview = (design: HeroDemo) => {
    // Track design preview
    analytics.page.designPreviewed(design.id, design.label)
    setPreviewDesign(design)
    setIsPreviewOpen(true)
  }

  const handleSearchSelect = (suggestion: { id: string; label: string }) => {
    // Track search selection
    analytics.search.performed(suggestion.label, 1, 'designs')
    // Just update the search query to filter the grid
    setSearchQuery(suggestion.label)
  }

  // Track search when user types (debounced effect)
  const lastSearchRef = useRef('')
  useEffect(() => {
    if (
      searchQuery &&
      searchQuery !== lastSearchRef.current &&
      filteredDesigns.length >= 0
    ) {
      lastSearchRef.current = searchQuery
      // Only track if user has typed at least 2 characters
      if (searchQuery.length >= 2) {
        analytics.search.performed(
          searchQuery,
          filteredDesigns.length,
          'designs'
        )
      }
    }
  }, [searchQuery, filteredDesigns.length])

  return (
    <>
      {/* Search Bar with Autocomplete */}
      <div className="mb-6">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('searchPlaceholder')}
          className="max-w-md mx-auto"
          suggestions={searchSuggestions}
          onSelect={handleSearchSelect}
        />
      </div>

      {/* Designs Grid */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <ComponentLoader size="lg" />
          <p className="text-muted-foreground text-sm">{t('loading')}</p>
        </div>
      ) : filteredDesigns.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {filteredDesigns.map((design, index) => (
            <DesignCard
              key={design.id}
              design={design}
              onClick={handleDesignSelect}
              onPreview={handleDesignPreview}
              priority={index < 5}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-10 sm:py-12 px-4">
          <p className="text-muted-foreground text-base sm:text-lg mb-3 sm:mb-4">
            {t('noDesignsFound')}
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="text-primary hover:underline font-medium text-sm sm:text-base cursor-pointer"
          >
            {t('clearSearch')}
          </button>
        </div>
      )}

      {/* Preview Drawer */}
      <DesignPreviewContainer
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        design={previewDesign}
        onSelect={handleDesignSelect}
      />
    </>
  )
}
